import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, Menu, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { navFor } from "./navigation";
import { initials } from "../../utils/format";
import { StatusBadge } from "../ui/Badge";

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const groups = navFor(user?.role);

    const handleLogout = () => {
        logout();
        navigate("/", { replace: true });
    };

    return (
        <div className="h-full flex flex-col">
            {/* Brand */}
            <div className="px-6 pt-6 pb-5">
                <img
                    src="/logo.png"
                    alt="Reztra"
                    className="h-10 object-contain drop-shadow-[0_0_15px_rgba(139,92,246,0.4)]"
                />
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-6">
                {groups.map((group) => (
                    <div key={group.title}>
                        <p className="px-3 mb-2 text-[10px] font-bold text-slate-600 uppercase tracking-[0.2em]">{group.title}</p>
                        <div className="space-y-1">
                            {group.items.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    onClick={onNavigate}
                                    className={({ isActive }) =>
                                        `relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                            isActive
                                                ? "text-white bg-[#8b5cf6]/15 border border-[#8b5cf6]/25"
                                                : "text-slate-400 hover:text-white hover:bg-slate-800/50 border border-transparent"
                                        }`
                                    }
                                >
                                    {({ isActive }) => (
                                        <>
                                            {isActive && (
                                                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-[#8b5cf6] shadow-[0_0_10px_#8b5cf6]" />
                                            )}
                                            <item.icon size={18} className={isActive ? "text-[#a78bfa]" : ""} />
                                            {item.label}
                                        </>
                                    )}
                                </NavLink>
                            ))}
                        </div>
                    </div>
                ))}
            </nav>

            {/* User card */}
            <div className="p-3 border-t border-slate-800">
                <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-800/30 border border-slate-800">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8b5cf6] to-[#6d28d9] flex items-center justify-center text-white text-sm font-bold shrink-0">
                        {initials(user?.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
                        <div className="mt-0.5 scale-90 origin-left">
                            <StatusBadge value={user?.role} />
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Logout"
                        aria-label="Logout"
                    >
                        <LogOut size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function AppLayout() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();

    // Close the drawer on navigation
    useEffect(() => setMobileOpen(false), [location.pathname]);

    return (
        <div className="min-h-screen bg-[#020617] text-slate-200 relative">
            {/* Background glows */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[#8b5cf6]/10 blur-[120px] rounded-full" />
                <div className="absolute bottom-[-10%] left-[20%] w-[400px] h-[400px] bg-[#8b5cf6]/5 blur-[120px] rounded-full" />
            </div>

            {/* Desktop sidebar */}
            <aside className="hidden lg:block fixed inset-y-0 left-0 w-72 z-30 bg-slate-950/60 backdrop-blur-2xl border-r border-slate-800">
                <SidebarContent />
            </aside>

            {/* Mobile top bar */}
            <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-[#020617]/80 backdrop-blur-xl border-b border-slate-800">
                <img src="/logo.png" alt="Reztra" className="h-8 object-contain" />
                <button
                    onClick={() => setMobileOpen(true)}
                    className="p-2 rounded-xl text-slate-300 hover:bg-slate-800 transition-colors"
                    aria-label="Open menu"
                >
                    <Menu size={22} />
                </button>
            </header>

            {/* Mobile drawer */}
            <AnimatePresence>
                {mobileOpen && (
                    <div className="lg:hidden fixed inset-0 z-40">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setMobileOpen(false)}
                            className="absolute inset-0 bg-[#020617]/80 backdrop-blur-sm"
                        />
                        <motion.aside
                            initial={{ x: "-100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "-100%" }}
                            transition={{ type: "spring", damping: 30, stiffness: 300 }}
                            className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-slate-950 border-r border-slate-800"
                        >
                            <button
                                onClick={() => setMobileOpen(false)}
                                className="absolute top-5 right-4 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800"
                                aria-label="Close menu"
                            >
                                <X size={20} />
                            </button>
                            <SidebarContent onNavigate={() => setMobileOpen(false)} />
                        </motion.aside>
                    </div>
                )}
            </AnimatePresence>

            {/* Page */}
            <main className="relative z-10 lg:pl-72">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}